import './EditableTheme.css';
import { useState, useEffect, useRef } from 'react';
import {
  Paper, Typography, Stack, Box, Skeleton,
  ToggleButtonGroup, ToggleButton, Switch, Divider, Slider, useMediaQuery,
} from '@mui/material';
import { TbArrowRight, TbArrowDownRight, TbArrowDown, TbArrowDownLeft, TbArrowUp } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import ColorField from '../ColorField';
import PresetCard from '../PresetCard';
import { ThemeSampleText, ThemeSampleButton, ThemeSampleBackground } from '../ThemeSamples/ThemeSamples';
import { useProfile } from '../../context/ProfileContext';
import { editTheme } from '../../reducer/profileActions';
import {
  THEME_PRESETS, BORDER_WIDTH_OPTIONS, BORDER_RADIUS_OPTIONS, GRADIENT_DIRECTIONS,
  THEME_CUSTOM_BG, THEME_FORM_INITIAL,
} from '../../utils/constants';
import { getPresetBg, mapThemeToForm } from '../../utils';

const DIRECTION_ICONS: Record<string, React.ComponentType> = {
  '90deg':  TbArrowRight,
  '135deg': TbArrowDownRight,
  '180deg': TbArrowDown,
  '225deg': TbArrowDownLeft,
  '0deg':   TbArrowUp,
};

type OptionLike = { id: number; label: string } | null;

interface ThemeForm {
  textPrimary: string;
  textSecondary: string;
  btnText: string;
  btnBg: string;
  btnBorderColor: string;
  btnShadowColor: string;
  bgSolid: string;
  bgLeft: string;
  bgRight: string;
  borderWidth: OptionLike;
  borderRadius: OptionLike;
  shadowEnabled: boolean;
  bgType: 'solid' | 'gradient';
  gradientDir: string;
}

const EditableTheme = () => {
  const { t } = useTranslation();
  const { state, dispatch, loading } = useProfile() as any;
  const theme = state.currentProfile.theme;
  const initialized = useRef(Object.keys(theme).length > 0);
  const themeRef = useRef(theme);
  const showSample = useMediaQuery('(max-width: 1023.95px)');

  useEffect(() => { themeRef.current = theme; }, [theme]);

  const [form, setForm] = useState<ThemeForm>(() =>
    Object.keys(theme).length ? mapThemeToForm(theme) : THEME_FORM_INITIAL as ThemeForm
  );

  const setField = <K extends keyof ThemeForm>(field: K, value: ThemeForm[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (initialized.current || !Object.keys(theme).length) return;
    setForm(mapThemeToForm(theme));
    initialized.current = true;
  }, [theme]);

  const setCustomPreset = () => {
    editTheme(dispatch, 'meta', { ...themeRef.current.meta, preset: 'custom' });
  };

  const knownPresets = ['custom', ...THEME_PRESETS.map(p => p.id)];
  const activePreset = knownPresets.includes(theme.meta?.preset) ? theme.meta.preset : 'custom';

  const applyPreset = (preset: typeof THEME_PRESETS[number]) => {
    setForm(mapThemeToForm(preset.theme));
    editTheme(dispatch, 'text', preset.theme.text);
    editTheme(dispatch, 'button', preset.theme.button);
    editTheme(dispatch, 'background', preset.theme.background);
    editTheme(dispatch, 'meta', { preset: preset.id, version: 2 });
  };

  const commitButton = (updates: Record<string, any>) => {
    editTheme(dispatch, 'button', { ...themeRef.current.button, ...updates });
    setCustomPreset();
  };

  const commitBackground = (updates: Record<string, any>) => {
    editTheme(dispatch, 'background', { ...themeRef.current.background, ...updates });
    setCustomPreset();
  };

  return (
    <Stack className="theme-stack" spacing={0}>

      {/* ── Presets ── */}
      <Paper variant="outlined" elevation={0} className="theme-section-card" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
          {t('profile.theme.presets.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {t('profile.theme.presets.subtitle')}
        </Typography>
        <Box className="preset-cards-row">
          {loading ? (
            [0, 1, 2, 3, 4].map(i => (
              <Skeleton key={i} variant="rounded" width={100} height={64} className="preset-card-skeleton" />
            ))
          ) : (
            <>
              <PresetCard
                id="custom"
                label={t('profile.theme.presets.custom')}
                isActive={activePreset === 'custom'}
                bg={THEME_CUSTOM_BG}
                textColor="rgba(255,255,255,1)"
              />
              {THEME_PRESETS.map(preset => (
                <PresetCard
                  key={preset.id}
                  id={preset.id}
                  label={t(preset.labelKey)}
                  isActive={activePreset === preset.id}
                  bg={getPresetBg(preset.theme)}
                  textColor={preset.theme.text.primary}
                  onClick={() => applyPreset(preset)}
                />
              ))}
            </>
          )}
        </Box>
      </Paper>

      {/* ── Text ── */}
      <Paper variant="outlined" elevation={0} className="theme-section-card" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
          {t('profile.theme.text.title')}
        </Typography>
        {showSample && !loading && (
          <Box className="theme-sample-wrap">
            <ThemeSampleText primary={form.textPrimary} secondary={form.textSecondary} />
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}
        <Stack divider={<Divider />} spacing={1.5}>
          <ColorField
            label={t('profile.theme.text.primary')}
            value={form.textPrimary}
            onChange={(val: string) => setField('textPrimary', val)}
            loading={loading}
            onCommit={() => {
              editTheme(dispatch, 'text', { ...themeRef.current.text, primary: form.textPrimary });
              setCustomPreset();
            }}
          />
          <ColorField
            label={t('profile.theme.text.secondary')}
            value={form.textSecondary}
            onChange={(val: string) => setField('textSecondary', val)}
            loading={loading}
            onCommit={() => {
              editTheme(dispatch, 'text', { ...themeRef.current.text, secondary: form.textSecondary });
              setCustomPreset();
            }}
          />
        </Stack>
      </Paper>

      {/* ── Buttons ── */}
      <Paper variant="outlined" elevation={0} className="theme-section-card" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
          {t('profile.theme.button.title')}
        </Typography>
        {showSample && !loading && (
          <Box className="theme-sample-wrap">
            <ThemeSampleButton form={form} />
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}
        <Stack spacing={1.5}>
          <Stack divider={<Divider />} spacing={1.5}>
            <ColorField
              label={t('profile.theme.button.text')}
              value={form.btnText}
              onChange={(val: string) => setField('btnText', val)}
              loading={loading}
              onCommit={() => commitButton({ text: form.btnText })}
            />
            <ColorField
              label={t('profile.theme.button.background')}
              value={form.btnBg}
              onChange={(val: string) => setField('btnBg', val)}
              loading={loading}
              onCommit={() => commitButton({ background: form.btnBg })}
            />
          </Stack>

          <Divider />

          {/* Border */}
          <Box>
            <Typography variant="caption" className="sub-section-label" sx={{ color: 'text.disabled' }}>
              {t('profile.theme.button.border.title')}
            </Typography>
            <Stack spacing={1.5}>
              <ColorField
                label={t('profile.theme.button.border.color')}
                value={form.btnBorderColor}
                onChange={(val: string) => setField('btnBorderColor', val)}
                loading={loading}
                onCommit={() => commitButton({ border: { ...themeRef.current.button?.border, color: form.btnBorderColor } })}
              />
              <Box className="border-sliders-stack">
                {loading ? (
                  <>
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="rounded" height={40} />
                  </>
                ) : (
                  <>
                    <Box className="border-slider-row">
                      <Box className="border-slider-row__header">
                        <Typography variant="caption" className="border-slider-row__label" sx={{ color: 'text.secondary' }}>
                          {t('profile.theme.button.border.width')}
                        </Typography>
                        <Typography variant="caption" className="border-slider-row__value">
                          {form.borderWidth?.label ?? BORDER_WIDTH_OPTIONS[0].label}
                        </Typography>
                      </Box>
                      <Slider
                        size="small"
                        value={Math.max(0, BORDER_WIDTH_OPTIONS.findIndex(o => o.label === form.borderWidth?.label))}
                        min={0}
                        max={BORDER_WIDTH_OPTIONS.length - 1}
                        step={1}
                        marks
                        onChange={(_, val) => {
                          const opt = BORDER_WIDTH_OPTIONS[val as number];
                          setField('borderWidth', opt);
                        }}
                        onChangeCommitted={(_, val) => {
                          const opt = BORDER_WIDTH_OPTIONS[val as number];
                          editTheme(dispatch, 'button', {
                            ...themeRef.current.button,
                            border: { ...themeRef.current.button?.border, width: opt.label },
                          });
                          setCustomPreset();
                        }}
                      />
                    </Box>
                    <Box className="border-slider-row">
                      <Box className="border-slider-row__header">
                        <Typography variant="caption" className="border-slider-row__label" sx={{ color: 'text.secondary' }}>
                          {t('profile.theme.button.border.radius')}
                        </Typography>
                        <Typography variant="caption" className="border-slider-row__value">
                          {form.borderRadius?.label ?? BORDER_RADIUS_OPTIONS[0].label}
                        </Typography>
                      </Box>
                      <Slider
                        size="small"
                        value={Math.max(0, BORDER_RADIUS_OPTIONS.findIndex(o => o.label === form.borderRadius?.label))}
                        min={0}
                        max={BORDER_RADIUS_OPTIONS.length - 1}
                        step={1}
                        marks
                        onChange={(_, val) => {
                          const opt = BORDER_RADIUS_OPTIONS[val as number];
                          setField('borderRadius', opt);
                        }}
                        onChangeCommitted={(_, val) => {
                          const opt = BORDER_RADIUS_OPTIONS[val as number];
                          editTheme(dispatch, 'button', {
                            ...themeRef.current.button,
                            border: { ...themeRef.current.button?.border, radius: opt.label },
                          });
                          setCustomPreset();
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Shadow */}
          <Box>
            <Box className={`sub-section-header${form.shadowEnabled ? ' sub-section-header--open' : ''}`}>
              <Typography variant="caption" className="sub-section-label sub-section-label--inline" sx={{ color: 'text.disabled' }}>
                {t('profile.theme.button.shadow.title')}
              </Typography>
              {loading
                ? <Skeleton variant="rounded" width={44} height={24} sx={{ borderRadius: '12px' }} />
                : <Switch
                    size="small"
                    checked={form.shadowEnabled}
                    onChange={(e) => {
                      setField('shadowEnabled', e.target.checked);
                      commitButton({ shadow: { ...themeRef.current.button?.shadow, enabled: e.target.checked } });
                    }}
                  />
              }
            </Box>
            {form.shadowEnabled && (
              <ColorField
                label={t('profile.theme.button.shadow.color')}
                value={form.btnShadowColor}
                onChange={(val: string) => setField('btnShadowColor', val)}
                loading={loading}
                onCommit={() => commitButton({
                  shadow: { ...themeRef.current.button?.shadow, enabled: true, color: form.btnShadowColor },
                })}
              />
            )}
          </Box>
        </Stack>
      </Paper>

      {/* ── Background ── */}
      <Paper variant="outlined" elevation={0} className="theme-section-card" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
          {t('profile.theme.background.title')}
        </Typography>
        {showSample && !loading && (
          <Box className="theme-sample-wrap">
            <ThemeSampleBackground form={form} />
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}
        <Stack spacing={1.5}>

          <Box className="bg-type-row">
            <Typography variant="subtitle2" className="bg-type-row__label" sx={{ color: 'text.secondary' }}>
              {t('profile.theme.background.type.label')}
            </Typography>
            {loading
              ? <Skeleton variant="rounded" width={160} height={36} sx={{ borderRadius: '8px' }} />
              : <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={form.bgType}
                  onChange={(_, val) => {
                    if (!val) return;
                    setField('bgType', val);
                    commitBackground({ type: val });
                  }}
                >
                  <ToggleButton value="solid" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                    {t('profile.theme.background.type.solid')}
                  </ToggleButton>
                  <ToggleButton value="gradient" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                    {t('profile.theme.background.type.gradient')}
                  </ToggleButton>
                </ToggleButtonGroup>
            }
          </Box>

          <Divider />

          {form.bgType === 'solid' ? (
            <ColorField
              label={t('profile.theme.background.solid')}
              value={form.bgSolid}
              onChange={(val: string) => setField('bgSolid', val)}
              loading={loading}
              onCommit={() => commitBackground({ solid: form.bgSolid })}
            />
          ) : (
            <Stack divider={<Divider />} spacing={1.5}>
              <ColorField
                label={t('profile.theme.background.gradient.left')}
                value={form.bgLeft}
                onChange={(val: string) => setField('bgLeft', val)}
                loading={loading}
                onCommit={() => commitBackground({ gradient: { ...themeRef.current.background?.gradient, left: form.bgLeft } })}
              />
              <ColorField
                label={t('profile.theme.background.gradient.right')}
                value={form.bgRight}
                onChange={(val: string) => setField('bgRight', val)}
                loading={loading}
                onCommit={() => commitBackground({ gradient: { ...themeRef.current.background?.gradient, right: form.bgRight } })}
              />
              <Box className="direction-row">
                <Typography variant="subtitle2" className="direction-row__label" sx={{ color: 'text.secondary' }}>
                  {t('profile.theme.background.gradient.direction')}
                </Typography>
                {loading
                  ? <Box className="direction-skeletons">
                      {[0, 1, 2, 3].map(i => (
                        <Skeleton key={i} variant="rounded" width={40} height={36} sx={{ borderRadius: '8px' }} />
                      ))}
                    </Box>
                  : <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={GRADIENT_DIRECTIONS.some(d => d.value === form.gradientDir) ? form.gradientDir : GRADIENT_DIRECTIONS[0].value}
                      onChange={(_, val) => {
                        if (!val) return;
                        setField('gradientDir', val);
                        commitBackground({
                          gradient: { ...themeRef.current.background?.gradient, direction: val },
                        });
                      }}
                    >
                      {GRADIENT_DIRECTIONS.map(dir => {
                        const DirIcon = DIRECTION_ICONS[dir.value];
                        return (
                          <ToggleButton key={dir.value} value={dir.value} sx={{ px: 1.5 }}>
                            <DirIcon />
                          </ToggleButton>
                        );
                      })}
                    </ToggleButtonGroup>
                }
              </Box>
            </Stack>
          )}
        </Stack>
      </Paper>

    </Stack>
  );
};

export default EditableTheme;
